import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React from "react";
import "../../../../css/Customer/Pages/OurServicesPage/Service.css";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as MortagePlanningIcon } from "../../../../assets/icons/Mortgage-Planning.svg";
import { ReactComponent as CorporatePlanningIcon } from "../../../../assets/icons/Corporate-Planning.svg";
import { ReactComponent as InvestmentPlanningIcon } from "../../../../assets/icons/Investment-Planning.svg";
import { ReactComponent as EquityReleaseIcon } from "../../../../assets/icons/Equity -Release.svg";
import { ReactComponent as InsuranceProtectionIcon } from "../../../../assets/icons/Insurance-Protection.svg";
import { ReactComponent as RetirementPlanningIcon } from "../../../../assets/icons/Retirement-Planning.svg";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import { ReactComponent as NumberOneIcon } from "../../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../../assets/icons/Number 4.svg";
import Testimonials from "../../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
// import CustomerRetirementImage from "../../../../../assets/images/customer-retirement-min.jpeg";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * EquityRelease component renders the Equity Release service page.
 *
 * This page provides information about equity release, including FAQs,
 * adviser values, and related services. It includes navigation, hero section,
 * detailed descriptions, a FAQ section, a list of related services, adviser values,
 * testimonials, contact form, and footer.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered Equity Release service page.
 */
const EquityRelease = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];
  const faqs = [
    {
      question: "What is equity release?",
      answer:
        "This is for homeowners aged 55 or older who can access the equity in their home which can be released. Our advisers can talk through the needs to free up some spare income.",
    },
    {
      question: "How much can I release?",
      answer:
        "This is dependent on your age, the value of your property and your health. There is a usual amount that can be released dependent on these factors.",
    },
    {
      question: "What costs are involved in equity release?",
      answer:
        "These include, but not limited to, arrangement fees, valuation fees and legal fees. There is risk attached to this and our advisers can find if this is the best plan for you.",
    },
    {
      question: "Do I still own my home with equity release?",
      answer:
        "Yes, you continue to own your home but the lender can now hold a charge over the home.",
    },
    {
      question: "Can I repay the loan early?",
      answer:
        "Some plans allow you to repay the loan early, but this can come at a charge. When discussing your plans we can assist to find the best plan for your payments.",
    },
  ];
  const values = [
    {
      icon: <NumberOneIcon />,
      text: "You are our most important person",
    },
    {
      icon: <NumberTwoIcon />,
      text: "Recommendations are planned out and delivered to you",
    },
    {
      icon: <NumberThreeIcon />,
      text: "Deliver the best outcomes for you",
    },
    {
      icon: <NumberFourIcon />,
      text: "We talk you through every step of the process",
    },
  ];

  return (
    <div className="Service">
      <SeoHelmet
        page={SEO_PAGES.equityRelease}
        service={{
          name: "Equity Release Advice",
          description:
            "Rosemount Financial Solutions provides equity release advice for homeowners aged 55 and over, helping clients understand lifetime mortgages, costs, risks, repayment options and suitable alternatives.",
          serviceType: [
            "Equity Release Advice",
            "Lifetime Mortgage Advice",
            "Property Wealth Planning",
            "Retirement Mortgage Advice",
            "Financial Advice",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Our Services",
            url: getPageUrl("/OurServices"),
          },
          {
            name: "Equity Release",
            url: getPageUrl("/OurServices/EquityRelease"),
          },
        ]}
        faqs={faqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["customer-equity-min"]}
        altText="A couple sat together cuddling and reading a hardback book."
        children={
          <>
            <h1>Equity Release</h1>
            <div className="SubTitle">
              Helping you realise your property wealth
            </div>
          </>
        }
      />
      <div className="SubTitle-Container">
        <div className="SubTitle">
          When you are looking to free up some equity in your home to help you
          with certain goals, this is the plan for you. Whether you are looking
          to release equity to fund a holiday, make home improvements or provide
          a gift to relatives, our advisers will ensure the right plan it put in
          place.
        </div>
        <div className="SubTitle">
          Speaking with an adviser can help you find the best way to free up
          some money with a tailored plan. Our equity release advisers are also
          mortgage advice qualified, so we can consider all the available
          options and ensure the most suitable plan is recommended.
        </div>
        <div className="SubTitle">
          Equity release is a realistic way for you to realise equity within
          your property. All the products our advisers offer include the no
          negative equity guarantee, so the outstanding debt will never increase
          above your property's value. Speak to us today to find out more
          information.
        </div>
        <div className="SubTitle">
          Equity release will reduce the value of your estate and can affect
          your eligibility for means tested benefits.
        </div>
      </div>
      <FAQContainer faqs={faqs} />
      <div className="Customer-Services-Container">
        <h2>Our Services</h2>
        <div className="Customer-Services">
          <CardProduct
            Icon={MortagePlanningIcon}
            title="Mortgage Planning"
            link="/OurServices/MortgagePlanning"
          />
          <CardProduct
            Icon={CorporatePlanningIcon}
            title="Corporate Planning"
            link="/OurServices/CorporatePlanning"
          />
          <CardProduct
            Icon={InvestmentPlanningIcon}
            title="Investment Planning"
            link="/OurServices/InvestmentPlanning"
          />
          <CardProduct
            Icon={RetirementPlanningIcon}
            title="Retirement Planning"
            link="/OurServices/RetirementPlanning"
          />

          <CardProduct
            Icon={InsuranceProtectionIcon}
            title="Insurance/ Protection"
            link="/OurServices/InsuranceProtection"
          />
        </div>
      </div>
      <AdviserValues title="Our Adviser Values" values={values} />
      <Testimonials />
      <ContactUs />
      <Footer />
    </div>
  );
};

export default EquityRelease;
